import type { ClipboardEvent, KeyboardEvent, MutableRefObject } from "react";

/** `playerAIMention`/`nextPhaseCommand`'s availability shape (PlayerAIRailProps). */
interface ComposerSuggestionAvailability {
  readonly available: boolean;
  readonly disabled?: boolean | undefined;
}

export interface ComposerTextareaProps {
  composerRef: MutableRefObject<HTMLTextAreaElement | null>;
  draft: string;
  setDraft: (value: string, caret: number, inputType?: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  composerPlaceholder?: string | undefined;
  composerDisabled: boolean;
  composerReadOnly: boolean;
  /** The mention picker is offered (Player AI and/or people). */
  mentionsAvailable: boolean;
  nextPhaseCommand?: ComposerSuggestionAvailability | undefined;
  mentionMenuOpen: boolean;
  slashMenuOpen: boolean;
  mentionListId: string;
  /** The active mention option's DOM id, while the picker is open. */
  mentionOptionId: string | undefined;
  slashListId: string;
  slashOptionId: string;
}

/**
 * The composer's raw `<textarea>` (PB-5422 chat-parts-private): `rows=1`,
 * `max-h-28`, `readOnly`-not-`disabled`, and the combobox role and aria
 * wiring for the two suggestion listboxes. Split into a sibling of
 * PlayerAIComposerInput.tsx, whose own `useLayoutEffect` still owns the
 * auto-grow sizing (over this same `composerRef`), to keep both files under
 * the 200-line soft cap (packages/ui/CLAUDE.md invariant 6). Moved as-is: no
 * class, DOM or behaviour change.
 */
export function ComposerTextarea({
  composerRef,
  draft,
  setDraft,
  onKeyDown,
  onPaste,
  composerPlaceholder,
  composerDisabled,
  composerReadOnly,
  mentionsAvailable,
  nextPhaseCommand,
  mentionMenuOpen,
  slashMenuOpen,
  mentionListId,
  mentionOptionId,
  slashListId,
  slashOptionId,
}: ComposerTextareaProps) {
  const suggests = mentionsAvailable || nextPhaseCommand?.available === true;
  return (
    <textarea
      ref={composerRef}
      role={suggests ? "combobox" : undefined}
      value={draft}
      onChange={(event) =>
        setDraft(
          event.target.value,
          event.target.selectionStart,
          (event.nativeEvent as InputEvent).inputType,
        )
      }
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      rows={1}
      placeholder={composerPlaceholder ?? "Ask PlayerAI…"}
      disabled={composerDisabled}
      // `readOnly`, never `disabled`: a disabled textarea cannot hold focus,
      // so setting it moves the caret to `<body>` mid-sentence and drops the
      // next keystroke. Read-only keeps the draft on screen, focused and
      // copyable, and only Send is refused.
      readOnly={composerReadOnly}
      aria-autocomplete={suggests ? "list" : undefined}
      aria-expanded={suggests ? mentionMenuOpen || slashMenuOpen : undefined}
      aria-controls={
        mentionMenuOpen
          ? mentionListId
          : slashMenuOpen
            ? slashListId
            : undefined
      }
      aria-activedescendant={
        mentionMenuOpen
          ? mentionOptionId
          : slashMenuOpen
            ? slashOptionId
            : undefined
      }
      className="flex-1 min-w-0 bg-transparent resize-none text-sm text-pb-text placeholder:text-pb-text-muted focus:outline-none max-h-[min(7rem,calc(20dvh-0.5rem))] leading-snug overflow-y-auto self-center"
    />
  );
}
